import _property from "lodash/property";
import _isFunction from "lodash/isFunction";
import _defaults from "lodash/defaults";
import React from "react";

import Collection from "./collection";

/**
 * creates an object with some keys excluded
 * replacement for lodash.omit for performance. does not mimick the entire lodash.omit api
 * @param {Object} originalObject: created object will be based on this object
 * @param {Array<String>} keys: an array of keys to omit from the new object
 * @returns {Object} new object with same properties as originalObject
 */
export var omit = function (originalObject) {
  var keys = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

  // code based on babel's _objectWithoutProperties
  var newObject = {};
  for (var key in originalObject) {
    if (keys.indexOf(key) >= 0) {
      continue;
    }
    if (!Object.prototype.hasOwnProperty.call(originalObject, key)) {
      continue;
    }
    newObject[key] = originalObject[key];
  }
  return newObject;
};

export default {
  getPoint: function (datum) {
    var exists = function (val) {
      return val !== undefined;
    };
    var _x = datum._x,
        _x1 = datum._x1,
        _x0 = datum._x0,
        _voronoiX = datum._voronoiX,
        _y = datum._y,
        _y1 = datum._y1,
        _y0 = datum._y0,
        _voronoiY = datum._voronoiY;

    var defaultX = exists(_x1) ? _x1 : _x;
    var defaultY = exists(_y1) ? _y1 : _y;
    var point = {
      x: exists(_voronoiX) ? _voronoiX : defaultX,
      x0: exists(_x0) ? _x0 : _x,
      y: exists(_voronoiY) ? _voronoiY : defaultY,
      y0: exists(_y0) ? _y0 : _y
    };
    return _defaults({}, point, datum);
  },
  scalePoint: function (props, datum) {
    var scale = props.scale,
        polar = props.polar;

    var d = this.getPoint(datum);
    var origin = props.origin || { x: 0, y: 0 };
    var x = scale.x(d.x);
    var x0 = scale.x(d.x0);
    var y = scale.y(d.y);
    var y0 = scale.y(d.y0);
    return {
      x: polar ? y * Math.cos(x) + origin.x : x,
      x0: polar ? y0 * Math.cos(x0) + origin.x : x0,
      y: polar ? -y * Math.sin(x) + origin.y : y,
      y0: polar ? -y0 * Math.sin(x0) + origin.x : y0
    };
  },
  formatPadding: function (padding) {
    var paddingVal = typeof padding === "number" ? padding : 0;
    var paddingObj = typeof padding === "object" ? padding : {};
    return {
      top: paddingObj.top || paddingVal,
      bottom: paddingObj.bottom || paddingVal,
      left: paddingObj.left || paddingVal,
      right: paddingObj.right || paddingVal
    };
  },
  getPadding: function (props) {
    return this.formatPadding(props.padding);
  },
  getStyles: function (style, defaultStyles) {
    var width = "100%";
    var height = "100%";
    if (!style) {
      return _defaults({ parent: { height: height, width: width } }, defaultStyles);
    }
    var data = style.data,
        labels = style.labels,
        parent = style.parent;

    var defaultParent = defaultStyles && defaultStyles.parent || {};
    var defaultLabels = defaultStyles && defaultStyles.labels || {};
    var defaultData = defaultStyles && defaultStyles.data || {};
    return {
      parent: _defaults({}, parent, defaultParent, { width: width, height: height }),
      labels: _defaults({}, labels, defaultLabels),
      data: _defaults({}, data, defaultData)
    };
  },
  evaluateProp: function (prop, data, active) {
    return _isFunction(prop) ? prop(data, active) : prop;
  },
  evaluateStyle: function (style, data, active) {
    var _this = this;

    if (!style || !Object.keys(style).some(function (value) {
      return _isFunction(style[value]);
    })) {
      return style;
    }
    return Object.keys(style).reduce(function (prev, curr) {
      prev[curr] = _this.evaluateProp(style[curr], data, active);
      return prev;
    }, {});
  },
  degreesToRadians: function (degrees) {
    return degrees * (Math.PI / 180);
  },
  radiansToDegrees: function (radians) {
    return radians / (Math.PI / 180);
  },
  getRadius: function (props) {
    var _getPadding = this.getPadding(props),
        left = _getPadding.left,
        right = _getPadding.right,
        top = _getPadding.top,
        bottom = _getPadding.bottom;

    var width = props.width,
        height = props.height;

    return Math.min(width - left - right, height - top - bottom) / 2;
  },
  getPolarOrigin: function (props) {
    var width = props.width,
        height = props.height;

    var _getPadding2 = this.getPadding(props),
        top = _getPadding2.top,
        bottom = _getPadding2.bottom,
        left = _getPadding2.left,
        right = _getPadding2.right;

    var radius = Math.min(width - left - right, height - top - bottom) / 2;
    var offsetWidth = width / 2 + left - right;
    var offsetHeight = height / 2 + top - bottom;
    return {
      x: offsetWidth + radius > width ? radius + left - right : offsetWidth,
      y: offsetHeight + radius > height ? radius + top - bottom : offsetHeight
    };
  },
  getRange: function (props, axis) {
    if (props.range && props.range[axis]) {
      return props.range[axis];
    } else if (props.range && Array.isArray(props.range)) {
      return props.range;
    }
    return props.polar ? this.getPolarRange(props, axis) : this.getCartesianRange(props, axis);
  },
  getCartesianRange: function (props, axis) {
    // determine how to lay the axis and what direction positive and negative are
    var isVertical = axis !== "x";
    var padding = this.getPadding(props);
    if (isVertical) {
      return [props.height - padding.bottom, padding.top];
    }
    return [padding.left, props.width - padding.right];
  },
  getPolarRange: function (props, axis) {
    if (axis === "x") {
      var startAngle = this.degreesToRadians(props.startAngle || 0);
      var endAngle = this.degreesToRadians(props.endAngle || 360);
      return [startAngle, endAngle];
    }
    return [props.innerRadius || 0, this.getRadius(props)];
  },
  createAccessor: function (key) {
    // creates a data accessor function
    // given a property key, path, array index, or null for identity.
    if (_isFunction(key)) {
      return key;
    } else if (key === null || key === undefined) {
      // null/undefined means "return the data item itself"
      return function (x) {
        return x;
      };
    }
    // otherwise, assume it is an array index, property key or path (_.property handles all three)
    return _property(key);
  },
  modifyProps: function (props, fallbackProps, role) {
    var theme = props.theme && props.theme[role] ? props.theme[role] : {};
    var themeProps = omit(theme, ["style"]);
    return _defaults({}, props, themeProps, fallbackProps);
  },


  // Axis helpers

  /**
   * Returns the given axis or the opposite axis when horizontal
   * @param {string} axis: the given axis, either "x" pr "y"
   * @param {Boolean} horizontal: true when the chart is flipped to the horizontal orientation
   * @returns {String} the dimension appropriate for the axis given its props "x" or "y"
   */
  getCurrentAxis: function (axis, horizontal) {
    var otherAxis = axis === "x" ? "y" : "x";
    return horizontal ? otherAxis : axis;
  },


  /**
   * @param {Object} props: axis component props
   * @returns {Boolean} true when the axis is vertical
   */
  isVertical: function (props) {
    var orientation = props.orientation || (props.dependentAxis ? "left" : "bottom");
    var vertical = { top: false, bottom: false, left: true, right: true };
    return vertical[orientation];
  },


  /**
   * @param {Object} props: axis component props
   * @returns {Boolean} true when tickValues contain strings
   */
  stringTicks: function (props) {
    return props.tickValues !== undefined && Collection.containsStrings(props.tickValues);
  },


  /**
   * @param {Array} children: an array of child components
   * @param {Function} iteratee: a function with arguments "child", "childName", and "parent"
   * @returns {Array} returns an array of results from calling the iteratee on all nested children
   */
  reduceChildren: function (children, iteratee) {
    var childIndex = 0;
    var traverseChildren = function (childArray, parent) {
      return childArray.reduce(function (memo, child) {
        var childName = child.props.name || childIndex;
        childIndex++;
        if (child.props && child.props.children) {
          var nestedChildren = React.Children.toArray(child.props.children);
          var nestedResults = traverseChildren(nestedChildren, child);
          memo = memo.concat(nestedResults);
        } else {
          var result = iteratee(child, childName, parent);
          memo = result ? memo.concat(result) : memo;
        }
        return memo;
      }, []);
    };
    return traverseChildren(children);
  }
};


//////////////////
// WEBPACK FOOTER
// ./~/victory-core/es/victory-util/helpers.js
// module id = 38
// module chunks = 0